<template>
    <flex-layout wrap align="center">
        <grid-layout v-if="breakPoint.smAndUp" class="col-lg-4 col-md-6 col-sm-4 col-xl-4 justify-content-start" :columnFraction="[2,1]" align="center">
            <base-text :size="labelSize" color="primary" no-wrap>{{ label }}</base-text>
            <base-checkbox v-model="enable" @update:modelValue="onCheckboxChange"/>
        </grid-layout>
        <div v-else class="d-flex col-4 align-content-center">
          <base-text class="w-100 align-self-center" :size="labelSize" color="primary" style="min-width: 100%!important;">{{ label }}</base-text>
          <base-checkbox v-model="enable" @update:modelValue="onCheckboxChange"/>
        </div>
    <flex-layout
        direction="row"
        justify="start"
        class="col-12 col-md-6 col-xl-8 pa-0 col-sm-8 col-lg-8"
        align="center"
    >
      <base-select :modelValue="minValue" placeholder="最小" :disabled="!enable" :items="minItems" :rules="rules"
                   :clearable="isMinClearable" @update:modelValue="onMinSelect"/>
      <div class="custom-col-1 font-weight-black">～</div>
      <base-select :modelValue="maxValue" placeholder="最大" :disabled="!enable" :items="maxItems" :rules="rules"
                   :clearable="isMaxClearable" @update:modelValue="onMaxSelect"/>
    </flex-layout>
  </flex-layout>
</template>

<script lang="ts">
const defaultPrices = [
  { text: '下限なし', value: 0 },
  { text: '500万円', value: 500 },
  { text: '1000万円', value: 1000 },
  { text: '1500万円', value: 1500 },
  { text: '2000万円', value: 2000 },
  { text: '2500万円', value: 2500 },
  { text: '3000万円', value: 3000 },
  { text: '3500万円', value: 3500 },
  { text: '4000万円', value: 4000 },
  { text: '4500万円', value: 4500 },
  { text: '5000万円', value: 5000 },
  { text: '5500万円', value: 5500 },
  { text: '6500万円', value: 6500 },
  { text: '7000万円', value: 7000 },
  { text: '7500万円', value: 7500 },
  { text: '8000万円', value: 8000 },
  { text: '9000万円', value: 9000 },
  { text: '１億円', value: 10000 },
  { text: '1.2億円', value: 12000 },
  { text: '上限なし', value: 999999 },
] as const;
type PriceItem = (typeof defaultPrices)[number];
type Price = typeof defaultPrices[number]['value'];
</script>
<script lang="ts" setup>
import {computed} from 'vue';
import BaseCheckbox from '@/components/Forms/BaseCheckbox.vue';
import BaseSelect from '@/components/Forms/BaseSelect.vue';
import FlexLayout from '@/components/Layouts/FlexLayout.vue';
import BaseText from '@/components/Texts/BaseText.vue';
import {ValidatorRule} from "@/composable/ui/useForm";
import {useBreakpoint} from "@/composable/ui/useScreen";
import GridLayout from "@/components/Layouts/GridLayout.vue";

const breakPoint = useBreakpoint();
const props = withDefaults(defineProps<{
  modelValue?: [number | undefined, number | undefined] | null,
  items?: PriceItem[],
  label?: string,
  strict?: boolean,
  rules?: ValidatorRule<[number | undefined, number | undefined] | null>[],
  min?: Price,
  max?: Price,
  labelSize?: 'xs' | 'sm' | 'md' | 'lg',
  minClearable?: boolean,
  maxClearable?: boolean,
}>(), {
  items: () => (defaultPrices as unknown as PriceItem[]),
  min: 0,
  max: 999999,
  rules: () => [] as ValidatorRule<[number | undefined, number | undefined] | null>[],
  strict: false,
  label: '',
  labelSize: 'xs',
  modelValue: null,
  minClearable: false,
  maxClearable: false,
});
const emit = defineEmits<{(event: 'update:modelValue', value: [number, number] | null): void}>()

const minValue = computed({
  get: () => props.modelValue?.[0] || props.min,
  set: (value: number) => {
    emit('update:modelValue', [value, maxValue.value])
  }
});
const maxValue = computed({
  get: () => props.modelValue?.[1] || props.max,
  set: (value: number) => emit('update:modelValue', [minValue.value, value]),
});
const maxItems =  computed(() => props.strict ? props.items.filter((item) => item.value > minValue.value) : props.items);
const minItems = computed(() => props.strict ? props.items.filter((item) => item.value < maxValue.value) : props.items);
const enable = computed(() => !!props.modelValue);
const isMinClearable = computed(() => !!(props.minClearable && minValue.value && minValue.value > 0));
const isMaxClearable = computed(() => !!(props.maxClearable && maxValue.value && maxValue.value < 999999));

function onMinSelect(value: number) {
  emit('update:modelValue', [value, maxValue.value]);
}
function onMaxSelect(value: number) {
  emit('update:modelValue', [minValue.value, value]);
}
function onCheckboxChange(value: boolean) {
  emit('update:modelValue', value ? [minValue.value, maxValue.value] : null);
}

</script>

<style scoped>

.custom-col-1{
  flex: 0 0 auto;
  width: 8%
}

</style>
